import { isApiError, unwrap, type ApiError } from "@sagot/api-client";
import { CONSOLE_PATHS, adminUrl, EMAIL_MAX_LENGTH, LEGAL_LINKS, PASSWORD_MAX_LENGTH, PRODUCT_NAME, TURNSTILE_ACTIONS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, Field, Icon, Input } from "@sagot/ui";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router";
import { z } from "zod";
import { useErrorMessage } from "../lib/api-errors";
import { zodResolver } from "../lib/zod-resolver";
import { clearPendingAuth } from "../session/pending-auth";
import { useApi } from "../session/session-provider";
import { AuthDivider } from "./components/auth-divider";
import { AuthLayout } from "./components/auth-layout";
import { FormAlert } from "./components/form-alert";
import { OAuthButton } from "./components/oauth-buttons";
import { PasswordInput } from "./components/password-input";
import { Turnstile } from "./components/turnstile";
import { SsoEntry } from "./sso-entry";
import { useCompleteSignIn } from "./use-sign-in";

function useLoginSchema() {
  const { t } = useTranslation();
  return z.object({
    email: z.string().trim().min(1, t("auth.common.emailRequired")).max(EMAIL_MAX_LENGTH).email(t("auth.common.emailInvalid")),
    password: z.string().min(1, t("auth.common.passwordRequired")).max(PASSWORD_MAX_LENGTH),
    remember: z.boolean(),
  });
}

type LoginValues = z.infer<ReturnType<typeof useLoginSchema>>;

function LoginError({ error, onDismiss }: { error: ApiError; onDismiss: () => void }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const resetLink = (
    <AppLink href={CONSOLE_PATHS.forgotPassword} className="font-semibold text-primary-ink hover:underline">
      {t("auth.login.resetLink")}
    </AppLink>
  );
  if (error.code === "account_locked") {
    return (
      <FormAlert title={t("auth.login.lockedTitle")} onDismiss={onDismiss} className="mt-5">
        {t("auth.login.lockedBody")} {resetLink}
      </FormAlert>
    );
  }
  if (error.code === "invalid_credentials") {
    const remaining = error.detailNumber("attempts_remaining");
    return (
      <FormAlert title={t("auth.login.errorTitle")} onDismiss={onDismiss} className="mt-5">
        {remaining !== undefined ? <>{t("auth.login.errorAttempts", { count: remaining })} </> : null}
        {resetLink}
      </FormAlert>
    );
  }
  return <FormAlert title={message(error)} onDismiss={onDismiss} className="mt-5" />;
}

function FooterLinks() {
  const { t } = useTranslation();
  return (
    <div className="mt-8 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-fg-subtle">
      <a href={LEGAL_LINKS.terms} className="hover:text-fg">
        {t("auth.common.terms")}
      </a>
      <a href={LEGAL_LINKS.privacy} className="hover:text-fg">
        {t("auth.common.privacy")}
      </a>
      <a href={LEGAL_LINKS.status} className="hover:text-fg">
        {t("auth.common.status")}
      </a>
    </div>
  );
}

export function LoginPage() {
  const { t } = useTranslation();
  const api = useApi();
  const [params] = useSearchParams();
  const completeSignIn = useCompleteSignIn();
  const [error, setError] = useState<ApiError | null>(null);
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaReset, setCaptchaReset] = useState(0);
  useEffect(clearPendingAuth, []);
  const { register, handleSubmit, formState, getValues } = useForm<LoginValues>({
    resolver: zodResolver(useLoginSchema()),
    defaultValues: { email: "", password: "", remember: true },
  });

  const onSubmit = handleSubmit(async (values) => {
    setError(null);
    try {
      const body = await unwrap(api.POST("/v1/auth/login", { body: { ...values, captcha_token: captchaToken } }));
      await completeSignIn(body, { email: values.email, next: params.get("next") });
    } catch (caught) {
      if (!isApiError(caught)) throw caught;
      setError(caught);
      if (caught.detailFlag("captcha_required") || caught.code.startsWith("captcha_")) setCaptchaRequired(true);
      setCaptchaReset((n) => n + 1);
    }
  });

  const emailInvalid = Boolean(formState.errors.email) || error?.code === "invalid_credentials";
  return (
    <AuthLayout
      title={t("auth.login.title", { product: PRODUCT_NAME })}
      prompt={{ text: t("auth.login.newHere", { product: PRODUCT_NAME }), label: t("auth.login.createAccount"), href: CONSOLE_PATHS.register }}
    >
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight">{t("auth.login.heading")}</h1>
        <p className="mt-1 text-fg-muted">{t("auth.login.subtitle")}</p>
      </div>
      {params.get("error") === "oauth" ? <FormAlert title={t("auth.login.oauthFailed")} className="mt-5" /> : null}
      {error ? (
        <LoginError
          error={error}
          onDismiss={() => {
            setError(null);
          }}
        />
      ) : null}
      <form className="mt-6 space-y-4" noValidate onSubmit={(event) => void onSubmit(event)}>
        <Field label={t("auth.common.workEmail")} error={formState.errors.email?.message}>
          <Input type="email" autoComplete="email" inputMode="email" className={emailInvalid ? "border-danger" : undefined} {...register("email")} />
        </Field>
        <div>
          <div className="flex items-center justify-between">
            <label className="label" htmlFor="login-password">
              {t("auth.common.password")}
            </label>
            <AppLink href={CONSOLE_PATHS.forgotPassword} className="mb-1.5 text-xs font-semibold text-primary-ink hover:underline">
              {t("auth.login.forgot")}
            </AppLink>
          </div>
          <PasswordInput
            id="login-password"
            autoComplete="current-password"
            aria-invalid={formState.errors.password ? true : undefined}
            aria-describedby={formState.errors.password ? "login-password-error" : undefined}
            {...register("password")}
          />
          {formState.errors.password ? (
            <p id="login-password-error" role="alert" className="mt-1.5 text-xs font-semibold text-danger">
              {formState.errors.password.message}
            </p>
          ) : null}
        </div>
        <label className="flex items-center gap-2 text-sm text-fg-muted">
          <input type="checkbox" className="checkbox" {...register("remember")} />
          {t("auth.login.remember")}
        </label>
        {captchaRequired ? <Turnstile action={TURNSTILE_ACTIONS.consoleLogin} onToken={setCaptchaToken} resetKey={captchaReset} /> : null}
        <Button type="submit" size="lg" className="w-full" loading={formState.isSubmitting} disabled={formState.isSubmitting || (captchaRequired && !captchaToken)}>
          {t("auth.login.submit")}
        </Button>
      </form>
      <AuthDivider label={t("auth.common.or")} />
      <div className="space-y-2.5">
        <OAuthButton provider="google" intent="login" label={t("auth.common.continueGoogle")} className="w-full" />
        <OAuthButton provider="microsoft" intent="login" label={t("auth.common.continueMicrosoft")} className="w-full" />
        <SsoEntry key={String(error?.code === "sso_enforced")} email={getValues("email")} enforced={error?.code === "sso_enforced"} />
      </div>
      <p className="mt-8 text-center text-sm text-fg-muted">
        {t("auth.login.newOrg")}{" "}
        <AppLink href={CONSOLE_PATHS.register} className="font-semibold text-primary-ink hover:underline">
          {t("auth.login.createAccount")}
        </AppLink>
      </p>
      <a
        href={adminUrl("/login")}
        className="mt-6 flex items-center gap-3 rounded-lg border border-line bg-surface px-4 py-3 hover:border-line-strong hover:bg-surface-2"
      >
        <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-sunken text-fg-muted">
          <Icon name="shield" className="size-4" />
        </span>
        <span className="min-w-0 flex-1 text-xs">
          <span className="block font-bold text-fg">{t("auth.login.staffTitle")}</span>
          <span className="text-fg-muted">{t("auth.login.staffBody")}</span>
        </span>
        <Icon name="arrow-right" className="size-4 text-fg-subtle" />
      </a>
      <FooterLinks />
    </AuthLayout>
  );
}
